import { useState } from 'react';
import { Report, ProjectGlobalData, ReportMetadata } from '../types';
import { applyLinkReturn, applyLinkTransfer, loadProjectById } from '../services/cloudService';
import { type LinkReturnPlan, type LinkTransferPlan } from '../services/projectLv';
import { LinkedBillingMode, billingModeLabelDe, switchBillingMode } from '../services/linkedBillingMode';
import { clearProjectLink, setLinkedBillingMode, setProjectLink } from '../services/projectLinkService';
import type { ListProject } from '../services/listProject';

/**
 * Was das Verknuepfen von der kuenftigen Mutter liest: Kennung und Name.
 * Bewusst schmal — der Dialog reicht den FRISCH geladenen Stand
 * (`loadProjectById`) herein, kein Eintrag der Projektliste; der Typ soll
 * beides tragen und nichts versprechen, was hier nicht gebraucht wird.
 */
export type MutterProjekt = Pick<ListProject, 'id' | 'name'> & {
    data?: { metadata?: Pick<ReportMetadata, 'projectName'> };
};

interface UseProjectLinkingProps {
    report: Report;
    globalData: ProjectGlobalData;
    onUpdate: (report: Report) => void;
    /**
     * Gerufen mit dem voll geladenen Mutterprojekt (`loadProjectById`) — es
     * traegt alle Spalten eines Listeneintrags; der Empfaenger (App.
     * handleOpenProject) laedt ohnehin selbst noch einmal voll.
     */
    onOpenProject?: (project: ListProject) => void;
    /**
     * Nur-Ansicht. Der Riegel steht hier UND an der Oberflaeche: der Banner
     * blendet seine Knoepfe aus, und dieser Hook schreibt trotzdem nichts,
     * falls ihn spaeter jemand von anderswo ruft. Ein Lesemodus, der nur aus
     * einem versteckten Knopf besteht, ist keiner.
     */
    isReadOnly?: boolean;
}

export const useProjectLinking = ({ report, globalData, onUpdate, onOpenProject, isReadOnly = false }: UseProjectLinkingProps) => {
    const [showLinkingModal, setShowLinkingModal] = useState(false);
    const [showUnlinkModal, setShowUnlinkModal] = useState(false);

    const handleOpenLinkedProject = async () => {
        const parentId = globalData.linkedProjectConfig?.linkedProjectId;
        if (parentId && onOpenProject) { try { const f = await loadProjectById(parentId); if (f) onOpenProject(f); else alert("Projekt nicht gefunden."); } catch (e) { console.error(e); } }
    };

    /**
     * Verknuepft dieses Projekt als Tochter — und haengt seine eigenen
     * Leistungen dabei an die Mutter um.
     *
     * Reihenfolge ist wesentlich:
     *   1. `applyLinkTransfer` schreibt relational (project_services). Wirft es,
     *      wird NICHT verknuepft — der Dialog zeigt die Meldung und bleibt offen.
     *   2. Erst danach wird der Report gesetzt, und zwar mit LEERER
     *      services-Liste. Ohne das Leeren schriebe der naechste Speichervorgang
     *      die Positionen aus dem Arbeitsspeicher wieder mit der eigenen
     *      project_id zurueck (saveProjectToCloud, childSyncs) und machte die
     *      Uebertragung stillschweigend rueckgaengig.
     *
     * Die leere Liste ist auch danach ungefaehrlich: pruneOrphans loescht bei
     * leerer Behalte-Liste ausdruecklich nichts, und als Tochter zieht sich das
     * Projekt das LV der Mutter ohnehin nur zur Anzeige heran
     * (projectHubLogic.resolveEffectiveServices).
     *
     * Dasselbe gilt fuer das LEISTUNGSPROTOKOLL: die Eintraege zu den
     * gewanderten Positionen gehoeren ab jetzt der Mutter. `applyLinkTransfer`
     * hat sie dort bereits ANGEHAENGT und liefert zurueck, was hier stehen
     * bleibt. Die Reihenfolge (erst anhaengen, dann entfernen) ist Absicht:
     * bricht es dazwischen ab, steht eine Zeile doppelt statt gar nicht.
     *
     *   3. DANN die Verknuepfung selbst — in die Spalten aus Nr. 71
     *      (`setProjectLink`), nicht in den Blob. Der Blob-Save schreibt sie
     *      nicht mehr (saveProjectToCloud streicht das Feld); ein veralteter
     *      Stand kann sie deshalb nicht mehr mitreissen (Vorfall 6290).
     *      Scheitert dieser Schritt, sind die Positionen schon umgezogen —
     *      der zweite Anlauf plant dann NULL Umzuege und setzt nur noch die
     *      Verknuepfung; der Fehlertext sagt das.
     */
    const handleLinkProject = async (
        parentProject: MutterProjekt,
        mode: 'daily_flat' | 'individual',
        plan?: LinkTransferPlan,
    ) => {
        // Derselbe Riegel wie beim Trennen — der Lesemodus gilt fuer BEIDE
        // Richtungen des Umbaus, nicht nur fuer die neue.
        if (isReadOnly) throw new Error('Nur-Ansicht: Es wird nichts geschrieben.');
        let serviceLog = globalData.serviceLog || [];
        if (plan && plan.total > 0) {
            const ergebnis = await applyLinkTransfer(parentProject.id, plan, {
                childProjectId: globalData.projectId || '',
                childProjectNumber: report.metadata?.projectNumber,
                childProjectName: report.metadata?.projectName,
                childServiceLog: serviceLog,
            });
            serviceLog = ergebnis.remainingChildLog;
        }
        try {
            await setProjectLink(globalData.projectId || '', parentProject.id, mode);
        } catch (e: any) {
            const umgezogen = plan && plan.total > 0;
            throw new Error(
                `${e?.message || e}` + (umgezogen
                    ? ' Die Leistungen sind bereits an das Mutterprojekt uebertragen, die Verknuepfung ist NICHT gesetzt — bitte erneut verknuepfen (es wird dann nichts mehr umgezogen).'
                    : ' Es wurde nichts veraendert.'),
            );
        }
        onUpdate({
            ...report,
            metadata: {
                ...report.metadata,
                globalData: {
                    ...globalData,
                    services: [],
                    serviceLog,
                    linkedProjectConfig: {
                        role: 'child',
                        linkedProjectId: parentProject.id,
                        linkedProjectName: parentProject.data?.metadata?.projectName || parentProject.name,
                        billingMode: mode,
                    },
                },
            },
        });
        setShowLinkingModal(false);
    };

    /**
     * Die Abrechnungsart der Tochter umstellen — ohne die Verknuepfung
     * anzuruehren.
     *
     * Bisher ging das nur beim Verknuepfen. Der Umweg „trennen und neu
     * verknuepfen" ist kein Ersatz: `applyLinkTransfer` haengt dabei die
     * Leistungen um, aus einer Einstellung wuerde ein Umbau.
     *
     * Geschrieben wird ueber `switchBillingMode`, das den STICHTAG festhaelt.
     * Nur so bleibt die Vergangenheit unberuehrt: eine Begehung wird nach der
     * Art beurteilt, die an ihrem Tag galt.
     *
     * Wirft nie — der Text landet im Dialog.
     */
    const handleChangeBillingMode = async (
        neu: LinkedBillingMode,
    ): Promise<{ ok: boolean; message: string }> => {
        // Wirft nie (Vertrag dieser Funktion) — der Riegel antwortet deshalb
        // wie jeder andere Fehlschlag hier: als Meldung.
        if (isReadOnly) return { ok: false, message: 'Nur-Ansicht: Es wird nichts geschrieben.' };
        const config = globalData.linkedProjectConfig;
        if (!config || config.role !== 'child') {
            return { ok: false, message: 'Die Abrechnungsart gibt es nur an einem Tochterprojekt.' };
        }
        const neueKonfiguration = switchBillingMode(config, neu);
        if (!neueKonfiguration) {
            return { ok: false, message: `Die Abrechnung steht bereits auf ${billingModeLabelDe(neu)}.` };
        }
        try {
            // ZUERST die Spalten (Nr. 71) — sie sind die Wahrheit. Der Blob-Save
            // danach traegt die Art nicht mehr; ohne diesen Schritt bliebe die
            // Umstellung eine Anzeige ohne Bestand.
            await setLinkedBillingMode(globalData.projectId || '', neueKonfiguration);
            onUpdate({
                ...report,
                metadata: {
                    ...report.metadata,
                    globalData: { ...globalData, linkedProjectConfig: neueKonfiguration },
                },
            });
            return { ok: true, message: `Abrechnung auf ${billingModeLabelDe(neu)} umgestellt.` };
        } catch (e: any) {
            return { ok: false, message: e?.message || 'Die Umstellung konnte nicht gespeichert werden.' };
        }
    };

    /**
     * Hebt die Verknuepfung auf — und holt die Leistungen dabei zurueck.
     *
     * Bis hierher stand hier ein `window.confirm` und ein `delete
     * globalData.linkedProjectConfig`. Die Positionen, die beim Verknuepfen an
     * die Mutter gewandert sind, blieben dort liegen: die Tochter stand danach
     * OHNE LV da, konnte nichts mehr buchen, und ihre erbrachten Leistungen
     * standen weiter im LV der Mutter, wo sie niemand mehr zuordnen konnte.
     * Verknuepfen war ein Umbau, Trennen bloss ein Schalter — die Asymmetrie
     * war der Fehler.
     *
     * Die Reihenfolge ist dieselbe wie beim Verknuepfen und aus demselben
     * Grund:
     *   1. `applyLinkReturn` schreibt relational (project_services) und
     *      bewegt das Protokoll. Wirft es, wird NICHT getrennt — der Dialog
     *      zeigt die Meldung und bleibt offen. Eine Tochter, deren
     *      Verknuepfung weg ist, waehrend ihr LV bei der Mutter liegt, waere
     *      der Zustand, den es gerade abzuschaffen gilt.
     *   2. Erst danach wird der Report gesetzt.
     *
     * `services` wird ERGAENZT und nicht ersetzt: die zurueckgeholten
     * Positionen kommen zu dem, was das Projekt im Speicher schon fuehrt. Eine
     * Tochter aus der Zeit vor dem Umzugswerkzeug hat ihre eigenen Positionen
     * noch selbst — wuerde die Liste ersetzt, prunte der naechste Speichervorgang
     * (saveProjectToCloud, childSyncs) genau sie weg.
     *
     * Der Herkunftsvermerk ist an den zurueckgegebenen Objekten bereits
     * geleert (siehe LinkReturnResult.returnedServices); ein stehengebliebener
     * Stempel wuerde beim naechsten Verknuepfen falsch gelesen.
     */
    const handleUnlinkProject = async (plan: LinkReturnPlan) => {
        if (isReadOnly) throw new Error('Nur-Ansicht: Es wird nichts geschrieben.');
        const config = globalData.linkedProjectConfig;
        if (!config || config.role !== 'child' || !config.linkedProjectId) {
            throw new Error('Dieses Projekt ist keine Tochter — es gibt nichts zu trennen.');
        }
        let serviceLog = globalData.serviceLog || [];
        let services = globalData.services || [];
        // AUCH bei einem Plan ohne Moves wird applyLinkReturn gerufen: nach
        // einem frueheren Teilerfolg, der alle Positionen umgehaengt hat und
        // erst am Protokoll scheiterte, plant der zweite Anlauf NULL Moves —
        // und genau dann muss der Abgleich in applyLinkReturn die verwaisten
        // Protokollzeilen bei der Mutter noch holen. Ohne etwas zu holen
        // schreibt der Aufruf nichts.
        if (plan) {
            // Nach `id` zusammengefuehrt und nicht bloss angehaengt: stuende
            // eine Position doppelt in der Liste, ginge sie als zwei Zeilen
            // mit derselben id in den Upsert — Postgres bricht den ganzen
            // Speichervorgang dann ab ("cannot affect row a second time").
            // Der Herkunftsvermerk ist hier schon geleert, wie ihn Schritt 1
            // von applyLinkReturn auch in der Datenbank leert.
            const nachId = new Map(services.map(s => [s.id, s]));
            for (const m of plan.moves) nachId.set(m.service.id, { ...m.service, transferredFrom: undefined });
            services = [...nachId.values()];
            const ergebnis = await applyLinkReturn(plan, {
                childProjectId: globalData.projectId || '',
                parentProjectId: config.linkedProjectId,
                // Der Bericht, den applyLinkReturn in Schritt 2 speichert,
                // traegt die zurueckkommenden Positionen BEREITS in seiner
                // Liste. Das ist kein Vorgriff, sondern der Riegel gegen den
                // Prune: saveProjectToCloud gleicht project_services ueber eine
                // Behalte-Liste ab (save_project_children_tx loescht Zeilen des
                // Projekts, deren id dort fehlt). Im Regelfall ist die Liste
                // einer Tochter leer (das Verknuepfen leert sie) und der Prune
                // bleibt aus — fuehrt die Tochter aber aus irgendeinem Altpfad
                // doch eigene Zeilen, loeschte derselbe Speichervorgang die
                // soeben zurueckgehaengten Positionen sonst wieder weg.
                //
                // Der SCHREIBWEG haelt das inzwischen selbst auf: applyLinkReturn
                // vermerkt die zurueckgegebenen ids, und saveProjectToCloud
                // ergaenzt sie in seiner Behalte-Liste (`umgezogeneLeistungen`
                // in cloudService.ts). Das Einmischen hier bleibt trotzdem
                // stehen — es haelt den Speicher des Aufrufers und die
                // Datenbank auf demselben Stand, und zwei Riegel gegen einen
                // Datenverlust sind einer zu wenig, nicht einer zu viel.
                childReport: {
                    ...report,
                    metadata: {
                        ...report.metadata,
                        globalData: { ...globalData, services },
                    },
                },
            });
            serviceLog = [...ergebnis.returnedLog, ...serviceLog];
            // Massgeblich bleibt, was applyLinkReturn BESTAETIGT hat — dieselben
            // Objekte, die jetzt in der Datenbank stehen.
            for (const s of ergebnis.returnedServices) nachId.set(s.id, s);
            services = [...nachId.values()];
        }
        // ERST die Positionen (oben), DANN die Spalten aus Nr. 71 leeren. Die
        // Reihenfolge ist Absicht: scheitert das Leeren, liegen die Positionen
        // schon bei der Tochter und die Verknuepfung steht noch — ein zweites
        // Trennen plant dann NULL Umzuege und leert nur noch die Spalten.
        // Umgekehrt (erst leeren, dann zurueckholen) staende bei einem Abbruch
        // eine Tochter ohne Verknuepfung UND ohne LV da — der Zustand vom
        // 02.09.2026.
        try {
            await clearProjectLink(globalData.projectId || '');
        } catch (e: any) {
            throw new Error(
                `${e?.message || e} Die Leistungen sind bereits zurueckgeholt, die Verknuepfung besteht noch — `
                + 'bitte erneut trennen (es wird dann nichts mehr umgezogen).',
            );
        }
        const g: ProjectGlobalData = { ...globalData, services, serviceLog };
        delete g.linkedProjectConfig;
        onUpdate({ ...report, metadata: { ...report.metadata, globalData: g } });
    };

    return {
        showLinkingModal, setShowLinkingModal,
        showUnlinkModal, setShowUnlinkModal,
        handleOpenLinkedProject,
        handleLinkProject,
        handleChangeBillingMode,
        handleUnlinkProject,
    };
};
